import React, { useEffect } from 'react';
import Logger from 'observability/Logger';
import { getInstance as getBootstrap } from 'utils/bootstrap';
import { decomposeColor, rgbToHex } from 'utils/colors';
import { brandedThemeFontsLoader } from 'utils/fontLoader';
import { GuestBrandedDefaultColors, GuestBrandedDefaultFonts } from './constants';

const LOG = new Logger('ApplyGuestBrandedTheme');

/**
 * Adjusts a hex color to be lighter or darker based on its brightness.
 * We only have 4 main colors from the brand, so this helps create hover/active states.
 *
 * @param hexColor
 * @param factor
 * @returns {`#${string}${string}${string}`}
 */
const adjustHexColorBasedOnBrightness = (hexColor, factor = 30) => {
    // Ensure the input is a valid hex color
    if (!/^#([0-9A-Fa-f]{6})$/.test(hexColor)) {
        throw new Error('Invalid hex color format. Use #RRGGBB.');
    }

    // Convert hex to RGB
    const r = parseInt(hexColor.slice(1, 3), 16);
    const g = parseInt(hexColor.slice(3, 5), 16);
    const b = parseInt(hexColor.slice(5, 7), 16);

    // Calculate brightness (perceived luminance)
    const brightness = 0.299 * r + 0.587 * g + 0.114 * b;

    // Determine adjustment direction
    const adjustment = brightness > 200 ? -factor : factor;

    // Adjust RGB values
    const newR = Math.min(255, Math.max(0, r + adjustment));
    const newG = Math.min(255, Math.max(0, g + adjustment));
    const newB = Math.min(255, Math.max(0, b + adjustment));

    // Convert back to hex
    const toHex = (value) => value.toString(16).padStart(2, '0');
    return `#${toHex(newR)}${toHex(newG)}${toHex(newB)}`;
};

// Prepares color variables based on appearance (light/dark)
export const prepareColors = (appearance) => {
    try {
        const colors = getBootstrap().get('brand.colors', {});

        const hexColors = Object.fromEntries(
            Object.entries(colors).map(([key, value]) => {
                const { values } = decomposeColor(value);
                const hexValue = rgbToHex(...values);
                return [key, hexValue];
            }),
        );

        if (appearance === 'dark') {
            return {
                background: hexColors.dark,
                text: hexColors.light,
                textHover: adjustHexColorBasedOnBrightness(hexColors.light, 50),
                action: hexColors.button,
                actionText: hexColors.light,
                actionHover: hexColors.buttonHover,
                actionActive: adjustHexColorBasedOnBrightness(hexColors.button, -20),
            };
        }

        return {
            background: hexColors.light,
            text: hexColors.dark,
            textHover: adjustHexColorBasedOnBrightness(hexColors.dark, 50),
            action: hexColors.button,
            actionText: hexColors.dark,
            actionHover: hexColors.buttonHover,
            actionActive: adjustHexColorBasedOnBrightness(hexColors.button, 20),
        };
    } catch (error) {
        LOG.error('Error preparing colors for guest branded theme:', error);
        return GuestBrandedDefaultColors;
    }
};

// Prepares font variables
export const prepareFonts = () => {
    try {
        const fonts = getBootstrap().get('brand.fonts', {});
        return {
            body: `${fonts.body?.name || ''}, ${GuestBrandedDefaultFonts.body}`,
            heading: `${fonts.heading?.name || ''}, ${GuestBrandedDefaultFonts.heading}`,
            subHeading: `${fonts.subHeading?.name || ''}, ${GuestBrandedDefaultFonts.subHeading}`,
            button: `${fonts.button?.name || ''}, ${GuestBrandedDefaultFonts.button}`,
        };
    } catch (error) {
        LOG.error('Error preparing fonts for guest branded theme:', error);
        return GuestBrandedDefaultFonts;
    }
};

// Loads the branded theme fonts and appends them to the document head
export const loadFonts = () => {
    try {
        const fonts = getBootstrap().get('brand.fonts', {});
        brandedThemeFontsLoader(Object.values(fonts));
    } catch (error) {
        LOG.error('Error loading fonts for guest branded theme:', error);
    }
};

const ApplyGuestBrandedTheme = ({ appearance }) => {
    const [theme, setTheme] = React.useState(null);

    useEffect(() => {
        if (theme !== appearance) {
            const colors = prepareColors(appearance);
            const fonts = prepareFonts();
            const guestBrand = window.document.querySelector('.guest-branded');
            if (guestBrand && guestBrand.style) {
                // Set color properties
                guestBrand.style.setProperty('--guest-branded-background', colors.background);
                guestBrand.style.setProperty('--guest-branded-text', colors.text);
                guestBrand.style.setProperty('--guest-branded-textHover', colors.textHover);
                guestBrand.style.setProperty('--guest-branded-action', colors.action);
                guestBrand.style.setProperty('--guest-branded-actionText', colors.actionText);
                guestBrand.style.setProperty('--guest-branded-actionHover', colors.actionHover);
                guestBrand.style.setProperty('--guest-branded-actionActive', colors.actionActive);
                // Set font properties
                guestBrand.style.setProperty('--guest-branded-font-body', fonts.body);
                guestBrand.style.setProperty('--guest-branded-font-heading', fonts.heading);
                guestBrand.style.setProperty('--guest-branded-font-subHeading', fonts.subHeading);
                guestBrand.style.setProperty('--guest-branded-font-button', fonts.button);
            }
            loadFonts();
            setTheme(appearance);
        }
    }, [theme, appearance]);
};

export default ApplyGuestBrandedTheme;
